import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Plus, Trash2, Check, PartyPopper, Coffee, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Birthday {
  id: string;
  name: string;
  day: number;
  relationship?: string;
  notes?: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const RELATIONSHIPS = [
  'Family', 'Friend', 'Colleague', 'Partner', 'Other'
];

const BirthdayMonthCapture: React.FC = () => {
  const { month } = useParams<{ month: string }>();
  const navigate = useNavigate();
  const [monthIndex, setMonthIndex] = useState(0);
  const [birthdays, setBirthdays] = useState<Birthday[]>([]);
  const [newName, setNewName] = useState('');
  const [newDay, setNewDay] = useState('');
  const [newRelationship, setNewRelationship] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (month) {
      const index = MONTHS.findIndex(m => m.toLowerCase() === month.toLowerCase());
      if (index !== -1) {
        setMonthIndex(index);
      }
    }
  }, [month]);

  useEffect(() => {
    // Load saved birthdays from localStorage
    const saved = localStorage.getItem(`birthdays-${monthIndex}`);
    if (saved) {
      setBirthdays(JSON.parse(saved));
    } else {
      setBirthdays([]);
    }
  }, [monthIndex]);

  const saveBirthdays = async (updatedBirthdays: Birthday[]) => {
    setBirthdays(updatedBirthdays);
    localStorage.setItem(`birthdays-${monthIndex}`, JSON.stringify(updatedBirthdays));

    // Save to D1 database via API
    const year = new Date().getFullYear();

    for (const b of updatedBirthdays) {
      const birthdayDate = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(b.day).padStart(2, '0')}`;

      try {
        const response = await fetch('https://birthday-coffee-api.b-9f2.workers.dev/api/birthdays', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: b.name,
            birthday: birthdayDate,
            relationship: b.relationship,
            notes: b.notes
          })
        });

        if (!response.ok) {
          console.error('Failed to save birthday to D1:', await response.text());
        }
      } catch (error) {
        console.error('Error saving birthday:', error);
      }
    }

    // Also keep localStorage sync for backward compatibility
    const hubContacts = updatedBirthdays.map(b => ({
      id: b.id,
      name: b.name,
      birthday: `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(b.day).padStart(2, '0')}`,
      relationship: b.relationship,
      notes: b.notes
    }));

    const existingStr = localStorage.getItem('birthdayContacts');
    const existing = existingStr ? JSON.parse(existingStr) : [];
    const otherMonths = existing.filter((c: any) => {
      const date = new Date(c.birthday);
      return date.getMonth() !== monthIndex;
    });

    const merged = [...otherMonths, ...hubContacts];
    localStorage.setItem('birthdayContacts', JSON.stringify(merged));
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();

    if (!newName.trim() || !newDay) return;

    const newBirthday: Birthday = {
      id: Date.now().toString(),
      name: newName.trim(),
      day: parseInt(newDay),
      relationship: newRelationship || undefined,
      notes: newNotes.trim() || undefined
    };

    const updated = [...birthdays, newBirthday].sort((a, b) => a.day - b.day);
    saveBirthdays(updated);

    // Reset form
    setNewName('');
    setNewDay('');
    setNewRelationship('');
    setNewNotes('');

    // Show success animation
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  const handleRemove = (id: string) => {
    const updated = birthdays.filter(b => b.id !== id);
    saveBirthdays(updated);
  };

  const handleFinish = () => {
    navigate('/hub');
  };

  const getDaysInMonth = (month: number) => {
    // For February, always allow up to 29 (leap year)
    // People born on Feb 29 should be able to add their birthday
    if (month === 1) { // February is index 1
      return 29;
    }
    const year = new Date().getFullYear();
    return new Date(year, month + 1, 0).getDate();
  };

  const daysInMonth = getDaysInMonth(monthIndex);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-8 h-8" />
            <h1 className="text-3xl md:text-4xl font-bold">
              {MONTHS[monthIndex]} Birthdays
            </h1>
          </div>
          <p className="text-orange-100 text-lg">
            Who else has a birthday in {MONTHS[monthIndex]}?
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Quick Entry Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <Plus className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-gray-800">Quick Add</h2>
            </div>

            <form onSubmit={handleAdd} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter name..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-400 transition-colors"
                  required
                />
              </div>

              {/* Day */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Day *
                </label>
                <input
                  type="number"
                  min="1"
                  max={daysInMonth}
                  value={newDay}
                  onChange={(e) => setNewDay(e.target.value)}
                  placeholder={`1-${daysInMonth}`}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-400 transition-colors"
                  required
                />
              </div>

              {/* Relationship */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Relationship
                </label>
                <select
                  value={newRelationship}
                  onChange={(e) => setNewRelationship(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-400 transition-colors"
                >
                  <option value="">Select...</option>
                  {RELATIONSHIPS.map(rel => (
                    <option key={rel} value={rel}>{rel}</option>
                  ))}
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes
                </label>
                <input
                  type="text"
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder="Coffee lover, likes flowers..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>

              {/* Submit */}
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-bold text-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Birthday
              </motion.button>
            </form>

            {/* Success Message */}
            <AnimatePresence>
              {showSuccess && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mt-4 p-4 bg-green-100 border-2 border-green-500 rounded-xl flex items-center gap-3"
                >
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-semibold">Birthday added!</span>
                  <PartyPopper className="w-5 h-5 text-green-600" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Birthdays List */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {birthdays.length} {birthdays.length === 1 ? 'Birthday' : 'Birthdays'}
                </h2>
                {birthdays.length > 0 && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleFinish}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Done
                  </motion.button>
                )}
              </div>

              {birthdays.length === 0 ? (
                <div className="text-center py-12">
                  <Coffee className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No birthdays added yet</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Use the form to add birthdays
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {birthdays.map((birthday, index) => (
                    <motion.div
                      key={birthday.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 bg-gradient-to-r from-orange-50 to-pink-50 rounded-xl border-2 border-orange-200"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                              {birthday.day}
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-800 text-lg">
                                {birthday.name}
                              </h3>
                              {birthday.relationship && (
                                <p className="text-sm text-gray-600">
                                  {birthday.relationship}
                                </p>
                              )}
                            </div>
                          </div>
                          {birthday.notes && (
                            <p className="text-sm text-gray-500 ml-13 mt-2">
                              {birthday.notes}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemove(birthday.id)}
                          className="p-2 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation */}
            {birthdays.length > 0 && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleFinish}
                className="w-full p-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl shadow-xl hover:shadow-2xl transition-all"
              >
                <div className="flex items-center justify-center gap-3">
                  <span className="text-lg font-bold">
                    Go to Birthday Hub
                  </span>
                  <ArrowRight className="w-6 h-6" />
                </div>
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BirthdayMonthCapture;
